import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Button from './Button';
import './AboutPage.css';

interface BoardMember {
  name: string;
  title: string;
  image: string;
}

const AboutPage: React.FC = () => {
  const boardMembers: BoardMember[] = [
    {
      name: "Lisa DeLong",
      title: "Founder",
      image: "/images/lisa-delong.jpg"
    },
    {
      name: "Robby DeLong",
      title: "Co-Founder",
      image: "/images/robby-delong.jpg"
    },
    {
      name: "Lauren DeLong",
      title: "Co-Founder",
      image: "/images/lauren-delong.jpg"
    }
  ];

  return (
    <div className="about-page">
      <Helmet>
        <title>About – Carly Faye Foundation</title>
        <meta name="description" content="Learn about the Carly Faye Foundation, our mission, and the legacy of Carly Faye DeLong." />
        <link rel="canonical" href="https://carly-faye-foundation.web.app/about" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About – Carly Faye Foundation" />
        <meta property="og:description" content="Learn about the Carly Faye Foundation, our mission, and Carly's story." />
        <meta property="og:url" content="https://carly-faye-foundation.web.app/about" />
        <meta property="og:image" content="/images/carly-faye-foundation-hero.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About – Carly Faye Foundation" />
        <meta name="twitter:description" content="Learn about the Carly Faye Foundation, our mission, and the legacy of Carly Faye DeLong." />
        <meta name="twitter:image" content="/images/carly-faye-foundation-hero.jpg" />
      </Helmet>
      {/* Carly's Story Section */}
      <section className="about-story-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="about-page-header"
          >
            <h1>About Our Foundation</h1>
          </motion.div>
          
          <div className="about-story-layout">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="about-story-content"
            >
              <span className="overline">Her Story</span>
              <h2>Carly Faye DeLong</h2>
              <p>
              On New Year's Eve 2016, Carly was diagnosed with Acute Lymphoblastic Leukemia (ALL). From the very start, she refused to let her diagnosis define her. After her first hospital stay, she returned home, looked in the mirror, and wrote: "You have been assigned this mountain to show others it can be moved."
              </p>
              <p>
              Whenever treatment allowed, Carly was back in the classroom and at volleyball practice—cheering on her teammates, traveling with the team, and holding on to her dream of returning to the court. Every setback only strengthened her resolve.
              </p>
              <p>
              Long before her diagnosis, Carly was known for her fierce determination, her ability to set big goals, and her passion for achieving them. That spirit never wavered.
              </p>
              <p>
              On September 23, 2017, Carly's courageous battle with ALL came to an end, but the impact of her life—and the inspiration she gave to everyone around her—continues to guide our mission every day.
              </p>
              <div className="story-quote">
                <blockquote>
                  "You Were Assigned This Mountain To Show Others It Can Be MOVED."
                </blockquote>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="about-story-images"
            >
              <div className="about-story-image-stack">
                <div className="about-story-image">
                  <img src="/images/carly/IMG_8204.JPG" alt="Carly Faye DeLong" />
                </div>
                <div className="about-story-image">
                  <img src="/images/carly/IMG_7695.JPG" alt="Carly Faye DeLong" />
                </div>
                <div className="about-story-image">
                  <img src="/images/carly/IMG_7705.JPG" alt="Carly Faye DeLong" />
                </div>
              </div>
              <div className="about-story-image-accent"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-mission-section">
        <div className="container">
          <div className="about-mission-layout">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="about-mission-image"
            >
              <div className="mission-image-container">
                <img src="/images/carly/IMG_2270.JPG" alt="Carly Faye Foundation" />
                <div className="mission-image-overlay"></div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="about-mission-content-stack"
            >
              <div className="about-mission-content">
                <span className="overline">Who are we</span>
                <h2>Our Foundation</h2>
                <p>
                  The Carly Faye Foundation honors the memory of Carly Faye, a vibrant young woman whose unwavering spirit and pursuit of her dreams inspired us all. We believe every girl deserves the opportunity to embrace her passions, set ambitious goals, and overcome any obstacles in her path, just as Carly did. Through programs and support, we empower young women to cultivate their dreams and achieve extraordinary things.
                </p>
              </div>

              <div className="about-mission-content">
                <span className="overline">Just Cause/Mission</span>
                <h2>Our Mission</h2>
                <p>
                  To inspire every young girl to embrace a life of passion, fueled by a spirit of dreaming big, setting ambitious goals, and possessing the courage to overcome any obstacle on her path to achieving them.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Board Section */}
      <section className="about-board-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <span className="overline">Our Team</span>
            <h2>The Board</h2>
            <p>
              We're dedicated to continuing the life of excellence, determination, and fearlessness led by Carly.
            </p>
          </motion.div>

          <div className="about-board-grid">
            {boardMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="about-board-card"
              >
                <div className="about-board-image">
                  <img src={member.image} alt={member.name} />
                </div>
                <div className="about-board-content">
                  <h3>{member.name}</h3>
                  <span className="about-board-title">{member.title}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-background">
          <div className="cta-overlay"></div>
        </div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>
              Join Our Mission
            </h2>
            <p>Help us support young women in their pursuit of academic and athletic excellence</p>
            <div className="cta-buttons">
              <Button href="/donate" variant="primary" size="large" icon={ArrowRight}>
                Make a Donation
              </Button>
              <Button href="/contact" variant="outline" size="large">
                Get Involved
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage; 